
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHosts, Host } from '@/hooks/useHosts';
import { useDebounce } from '@/hooks/useDebounce';

interface PropertyHostSelectorProps {
  selectedHost: Host | null;
  onHostSelect: (host: Host | null) => void;
}

const PropertyHostSelector = ({ selectedHost, onHostSelect }: PropertyHostSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  const { data: hosts = [], isLoading } = useHosts(debouncedSearchTerm);

  return (
    <div>
      <Label>Anfitrión</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedHost ? (
              <div className="flex items-center">
                <User className="mr-2 h-4 w-4" />
                {selectedHost.name}
                {selectedHost.super_host && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    Super Host
                  </span>
                )}
              </div>
            ) : (
              "Seleccionar anfitrión..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput 
              placeholder="Buscar anfitrión..." 
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Cargando anfitriones...
                </div>
              ) : (
                <>
                  <CommandEmpty>No se encontraron anfitriones.</CommandEmpty>
                  <CommandGroup>
                    {hosts.map((host) => (
                      <CommandItem
                        key={host.id}
                        value={host.name}
                        onSelect={() => {
                          onHostSelect(selectedHost?.id === host.id ? null : host);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedHost?.id === host.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex items-center">
                          <User className="mr-2 h-4 w-4" />
                          <div>
                            <div className="font-medium">{host.name}</div>
                            {host.super_host && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                Super Host
                              </span>
                            )}
                            {host.experience && (
                              <div className="text-xs text-gray-500 mt-1">{host.experience}</div>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PropertyHostSelector;
